import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Trash2Icon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRole } from '@/hooks/use-role';
import { employeesQueryKey, listEmployees } from '@/lib/api/employees';
import {
  assignJobVisitEmployee,
  jobVisitEmployeesQueryKey,
  listJobVisitEmployees,
  unassignJobVisitEmployee,
  updateJobVisitEmployee,
  type AssignJobVisitEmployeeRequest,
} from '@/lib/api/jobs';

import { assignVisitEmployeeSchema, type AssignVisitEmployeeInput } from './schema';

interface Props {
  businessId: string;
  jobId: string;
  visitId: string;
}

const DEFAULT_VALUES: AssignVisitEmployeeInput = { employeeId: '', payAmount: '' };

// Per-visit crew editor. Self-contained: owns its own list query + assign /
// update-pay / unassign mutations. The global MutationCache handles toasts
// (`meta.successMessage`) and refetching. Rendered inline for a one-time job
// and inside each expanded visit row of a series.
export function VisitCrewSection({ businessId, jobId, visitId }: Props) {
  const { t } = useLanguage();
  // The visit endpoints are owner-only, so assignment rights track the role.
  const { isOwner: canAssign } = useRole();

  const assignedQuery = useQuery({
    queryKey: jobVisitEmployeesQueryKey(businessId, jobId, visitId),
    queryFn: () => listJobVisitEmployees(businessId, jobId, visitId),
  });

  const employeesQuery = useQuery({
    queryKey: employeesQueryKey(businessId, 'active'),
    queryFn: () => listEmployees(businessId, 'active'),
  });

  const assigned = useMemo(() => assignedQuery.data ?? [], [assignedQuery.data]);

  const availableEmployees = useMemo(() => {
    const taken = new Set(assigned.map((a) => a.employee_id));
    return (employeesQuery.data ?? []).filter((e) => !taken.has(e.id));
  }, [assigned, employeesQuery.data]);

  const addForm = useForm<AssignVisitEmployeeInput>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(assignVisitEmployeeSchema),
  });

  const assignMutation = useMutation({
    mutationFn: (body: AssignJobVisitEmployeeRequest) =>
      assignJobVisitEmployee(businessId, jobId, visitId, body),
    meta: { successMessage: t.jobEmployeeAssigned },
    onSuccess: () => addForm.reset(DEFAULT_VALUES),
  });

  const updatePayMutation = useMutation({
    mutationFn: ({ id, payAmount }: { id: string; payAmount: number }) =>
      updateJobVisitEmployee(businessId, jobId, visitId, id, { pay_amount: payAmount }),
    meta: { successMessage: t.jobEmployeePayUpdated },
  });

  const unassignMutation = useMutation({
    mutationFn: (assignmentId: string) =>
      unassignJobVisitEmployee(businessId, jobId, visitId, assignmentId),
    meta: { successMessage: t.jobEmployeeRemoved },
  });

  // Two-step unassign: first click arms the row, a second click within the
  // timeout confirms. No shared confirm-dialog component exists in the app.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    };
  }, []);

  function requestUnassign(assignmentId: string) {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    if (confirmingId === assignmentId) {
      setConfirmingId(null);
      unassignMutation.mutate(assignmentId);
      return;
    }
    setConfirmingId(assignmentId);
    confirmTimerRef.current = setTimeout(() => setConfirmingId(null), 4000);
  }

  function handleAssign(data: AssignVisitEmployeeInput) {
    assignMutation.mutate({
      employee_id: data.employeeId,
      pay_amount: data.payAmount ? Number(data.payAmount) : undefined,
    });
  }

  function commitPay(id: string, current: number | null, raw: string) {
    const trimmed = raw.trim();
    if (trimmed === '') return;
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0 || n === current) return;
    updatePayMutation.mutate({ id, payAmount: n });
  }

  const busy =
    assignMutation.isPending ||
    updatePayMutation.isPending ||
    unassignMutation.isPending;

  return (
    <div className="flex flex-col gap-3" data-testid="visit-crew-section">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {t.jobTeam}
      </h4>

      {assignedQuery.isPending ? (
        <p className="text-sm text-muted-foreground">{t.loading}</p>
      ) : assigned.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.jobNoAssignedEmployees}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {assigned.map((row) => (
            <li
              key={row.id}
              className="flex items-center gap-3 text-sm"
              data-testid={`visit-employee-${row.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="truncate">{row.employee_full_name}</p>
                {row.employee_email && (
                  <p className="truncate text-xs text-muted-foreground">
                    {row.employee_email}
                  </p>
                )}
              </div>
              <Input
                key={`${row.id}-${row.pay_amount ?? ''}`}
                type="number"
                min={0}
                step="0.01"
                defaultValue={row.pay_amount ?? ''}
                disabled={!canAssign || busy}
                aria-label={t.jobPayAmount}
                data-testid={`visit-employee-pay-${row.id}`}
                className="w-24"
                onBlur={(e) => commitPay(row.id, row.pay_amount, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
              />
              {canAssign &&
                (confirmingId === row.id ? (
                  <button
                    type="button"
                    onClick={() => requestUnassign(row.id)}
                    disabled={busy}
                    data-testid={`visit-unassign-${row.id}`}
                    className="text-xs font-medium text-destructive hover:underline rounded-md px-1 py-1 disabled:opacity-50"
                  >
                    {t.jobUnassignConfirm}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => requestUnassign(row.id)}
                    disabled={busy}
                    data-testid={`visit-unassign-${row.id}`}
                    aria-label={t.jobUnassign}
                    className="text-muted-foreground hover:text-destructive transition-colors rounded-md p-1 disabled:opacity-50"
                  >
                    <Trash2Icon className="w-4 h-4" />
                  </button>
                ))}
            </li>
          ))}
        </ul>
      )}

      {canAssign &&
        (availableEmployees.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t.jobAllEmployeesAssigned}</p>
        ) : (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-end gap-2">
              <Controller
                control={addForm.control}
                name="employeeId"
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                    <Label htmlFor={`assign-employee-${visitId}`}>
                      {t.jobAssignEmployee}
                    </Label>
                    <select
                      {...field}
                      id={`assign-employee-${visitId}`}
                      data-testid="assign-employee-select"
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">{t.jobSelectEmployee}</option>
                      {availableEmployees.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              />
              <Controller
                control={addForm.control}
                name="payAmount"
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5 w-24">
                    <Label htmlFor={`assign-pay-${visitId}`}>{t.jobPayAmount}</Label>
                    <Input
                      {...field}
                      id={`assign-pay-${visitId}`}
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder={t.jobPayAmountPlaceholder}
                      data-testid="assign-pay-input"
                    />
                  </div>
                )}
              />
              <Button
                type="button"
                size="sm"
                disabled={assignMutation.isPending}
                data-testid="assign-employee-add"
                onClick={() => {
                  void addForm.handleSubmit(handleAssign)();
                }}
              >
                {t.jobAssignAdd}
              </Button>
            </div>
            {addForm.formState.errors.employeeId && (
              <p className="text-xs text-destructive">
                {addForm.formState.errors.employeeId.message}
              </p>
            )}
            {addForm.formState.errors.payAmount && (
              <p className="text-xs text-destructive">
                {addForm.formState.errors.payAmount.message}
              </p>
            )}
          </div>
        ))}
    </div>
  );
}
